import { forwardRef, useState } from 'react'
import clsx from 'clsx'

// Spelled out so Tailwind keeps the classes
const GRID_COLUMNS = { 4: 'grid-cols-4', 6: 'grid-cols-6', 8: 'grid-cols-8' } as const

interface CodeInputProps extends Omit<
	React.InputHTMLAttributes<HTMLInputElement>,
	'value' | 'onChange' | 'type' | 'maxLength'
> {
	value: string
	/** Receives digits only, at most `length` of them */
	onChange: (value: string) => void
	length?: keyof typeof GRID_COLUMNS
	invalid?: boolean
}

/**
 * One-time code field: a single real input (so paste, SMS/email autofill and screen readers see one field)
 * drawn as one box per digit.
 */
const CodeInput = forwardRef<HTMLInputElement, CodeInputProps>(
	(
		{ value, onChange, length = 6, invalid = false, disabled, className, onFocus, onBlur, onSelect, ...props },
		ref
	) => {
		const [focused, setFocused] = useState(false)
		// The real input is invisible, so its caret / selection is mirrored onto the boxes
		const [selection, setSelection] = useState<[number, number]>([value.length, value.length])
		// Clamped: a value cleared from outside (wrong code) leaves an old, larger selection behind
		const selectionStart = Math.min(selection[0], value.length)
		const selectionEnd = Math.min(selection[1], value.length)
		const isActive = (index: number) =>
			focused &&
			(selectionStart === selectionEnd
				? index === Math.min(selectionStart, length - 1)
				: index >= selectionStart && index < selectionEnd)

		const syncSelection = (input: HTMLInputElement) =>
			setSelection([input.selectionStart ?? input.value.length, input.selectionEnd ?? input.value.length])

		return (
			<div className={clsx('relative', className)}>
				<input
					ref={ref}
					type="text"
					inputMode="numeric"
					autoComplete="one-time-code"
					pattern="[0-9]*"
					spellCheck={false}
					value={value}
					// No maxLength: it would cut a pasted "123 456" before the spaces are stripped
					onChange={event => onChange(event.target.value.replace(/\D/g, '').slice(0, length))}
					onFocus={event => {
						setFocused(true)
						syncSelection(event.currentTarget)
						onFocus?.(event)
					}}
					// Fires on every caret move (arrows, clicks, Home/End) and after each edit
					onSelect={event => {
						syncSelection(event.currentTarget)
						onSelect?.(event)
					}}
					onBlur={event => {
						setFocused(false)
						onBlur?.(event)
					}}
					disabled={disabled}
					aria-invalid={invalid || undefined}
					// text-base keeps iOS from zooming in on focus; the text itself is drawn by the boxes below
					className="absolute inset-0 z-10 size-full cursor-text text-base opacity-0 disabled:cursor-not-allowed"
					{...props}
				/>
				<div aria-hidden="true" className={clsx('pointer-events-none grid gap-2', GRID_COLUMNS[length])}>
					{Array.from({ length }, (_, index) => (
						<div
							key={index}
							className={clsx(
								'flex h-12 items-center justify-center rounded-lg border-2 bg-primary-surface/[0.4] text-xl font-semibold text-primary-text backdrop-blur-lg transition-colors',
								{
									'border-secondary-red': invalid,
									'border-accent-surface': !invalid && isActive(index),
									'border-translucent-plus-border': !invalid && !isActive(index),
									// The red border can't show focus, so an invalid field marks the caret with a ring
									'ring-2 ring-accent-surface ring-offset-2 ring-offset-primary-surface':
										invalid && isActive(index),
									'opacity-50': disabled,
								}
							)}
						>
							{value[index] ?? ''}
						</div>
					))}
				</div>
			</div>
		)
	}
)
CodeInput.displayName = 'CodeInput'

export { CodeInput }
export type { CodeInputProps }
